import { Chip, ChipTone } from "./Chip";
import { FIX_APPLIED_HINT, INCIDENT_STATUS_DISPLAY } from "../vocabulary";
import type { IncidentLite, IncidentStatus } from "../types";

/**
 * Tones for the statuses that reach a chip. Every key here is non-`active` by
 * construction — see the class rule below — so the map has no entry for the
 * five active statuses rather than dead entries claiming to be render rules.
 */
const STATUS_TONE: Partial<Record<IncidentStatus, ChipTone>> = {
  fix_applied: "teal",
  verified: "green",
  closed_left_fleet: "neutral",
  closed_signal_retracted: "neutral",
};

const STATUS_HINT: Partial<Record<IncidentStatus, string>> = {
  fix_applied: FIX_APPLIED_HINT,
};

/**
 * The incident status chip — **an OUTCOME only, never a phase of our plan.**
 *
 * The rule is `statusClass`, not a list of statuses: an `active` incident gets
 * no chip at all, and `awaiting-verification` / `closed` get theirs. Reading it
 * off the class means a status added to the wire later inherits the right
 * behaviour without an edit here, and there is no hide-list to keep in sync.
 *
 * This is the 2026-08-17 narrowing, and the line it draws is the one the
 * earlier rounds kept missing. The chip used to render every status it was not
 * explicitly told to hide, which put `Open`, `Diagnosing`, `Fix identified`,
 * `Research queue` and `Waiting on others` on operator surfaces — five ways of
 * saying which phase OUR fix plan had reached. That is a fact about our
 * records, it changes nothing the reader should do, and `waiting_on_others` was
 * actively misleading besides: the backend derives it while the step is still
 * `pending`, so it claimed a hand-off nobody had made (two rounds of rewording,
 * §11.9/50 and the `handOffPhase` split, both now retired). Where an operator
 * needs the plan's phase they read the steps themselves, which carry their own
 * `By hand` / `Automatic` tag beside the button that acts on them.
 *
 * `Fix applied` survives that cut because it is not a phase — it is a claim
 * that the work is DONE and the hotel is proving it, and the reader's correct
 * response ("leave it, don't log a second fix") is not available anywhere else
 * on a row. `Verified` and the two machine closes are outcomes for the same
 * reason.
 */
export function IncidentStatusChip({
  incident,
}: {
  incident: Pick<IncidentLite, "status" | "statusClass">;
}) {
  if (incident.statusClass === "active") return null;
  return (
    <Chip
      tone={STATUS_TONE[incident.status] ?? "neutral"}
      title={STATUS_HINT[incident.status]}
    >
      {INCIDENT_STATUS_DISPLAY[incident.status]}
    </Chip>
  );
}
